import React, { useCallback, useEffect, useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import {
  SingleDropdown,
  SimpleSingleDropdown,
  MultiDropdown,
  useSingleDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  standardRequestWithErrorFlash,
  standardRequestWithFlash,
  standardRequest,
} from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import {
  toMultiDropdownOptions,
  toNestedDropdownOptions,
  type Option,
} from 'shared/utils/formUtils';
import {
  SecondaryBody,
  SectionHeader,
} from '@grnhse/seedling/lib/birch/typography';
import Divider from '@grnhse/seedling/lib/birch/components/divider';

const t = withLocalePrefix('talent_matching.rediscovery.table.prospect_modal');

type ProspectPool = {
  id: number;
  name: string;
  prospect_pool_stages: Array<{ id: number; name: string }>;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  makeProspectPath: string;
  fullName: string;
  jobsPath: string;
  departmentsPath: string;
  officesPath: string;
  prospectPoolsPath: string;
  prospectOwnerOptionsPath: string;
  sourceOptionsPath: string;
  referrerOptionsPath: string;
  defaultJob: Item | null;
  applicationId: string;
  onSuccess?: (metadata: Record<string, unknown>) => void;
  sourceTypeOptionsPath?: string;
  canCreateSources?: boolean;
  defaultPoolName?: string | null;
  defaultOwner?: Item | null;
  sourceAndResponsibilityPath?: string;
};

const toOptions = (results: Option[], nested = false) => {
  if (nested) {
    const nonEmpty = results.filter(
      (opt) => !opt.children || opt.children.length > 0
    );
    return toNestedDropdownOptions(nonEmpty);
  }
  return toMultiDropdownOptions(results);
};

const extractItems = (data: Record<string, unknown>): Option[] => {
  if (data.results) {
    return data.results as Option[];
  }
  if (data.items) {
    return data.items as Option[];
  }
  if (data.data) {
    return data.data as Option[];
  }
  return data as unknown as Option[];
};

const CACHE_STALE_TIME = 1000 * 60 * 5; // 5 minutes

const useFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>,
  onRawData?: (rawItems: Option[]) => void,
  nested = false
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (term: string): Promise<{ items: Item[] }> => {
      const queryKey = ['prospect-modal', path, term, params];

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return { items: toOptions(rawItems, nested), rawItems };
        },
        staleTime: CACHE_STALE_TIME,
      });

      onRawData?.(result.rawItems);
      return { items: result.items };
    },
    [path, params, onRawData, queryClient, nested]
  );
};

const usePaginatedFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      term: string,
      nextPage: number
    ): Promise<{ items: Item[]; more: boolean }> => {
      const nested = Boolean(params?.nested);
      const queryKey = ['prospect-modal', path, term, nextPage, params];

      const cached = queryClient.getQueryData<{ items: Item[]; more: boolean }>(
        queryKey
      );
      if (cached) {
        return cached;
      }

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: nextPage || 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return {
            items: toOptions(rawItems, nested),
            more: response.data.more ?? false,
          };
        },
        staleTime: CACHE_STALE_TIME,
      });

      return result;
    },
    [path, params, queryClient]
  );
};

const MakeProspectModal = ({
  isOpen,
  closeModal,
  makeProspectPath,
  fullName,
  jobsPath,
  departmentsPath,
  officesPath,
  prospectPoolsPath,
  prospectOwnerOptionsPath,
  sourceOptionsPath,
  referrerOptionsPath,
  defaultJob,
  applicationId,
  onSuccess,
  sourceTypeOptionsPath,
  canCreateSources = false,
  defaultPoolName,
  defaultOwner,
  sourceAndResponsibilityPath,
}: Props) => {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const [selectedJobs, setSelectedJobs] = useState<Item[]>(
    defaultJob ? [defaultJob] : []
  );
  const [selectedDepartment, setSelectedDepartment] = useState<Item | null>(
    null
  );
  const [selectedOffice, setSelectedOffice] = useState<Item | null>(null);
  const [selectedPool, setSelectedPool] = useState<Item | null>(null);
  const [selectedStage, setSelectedStage] = useState<Item | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<Item | null>(
    defaultOwner ?? null
  );
  const [selectedSource, setSelectedSource] = useState<Item | null>(null);
  const [selectedReferrer, setSelectedReferrer] = useState<Item | null>(null);

  // Custom source/referrer state
  const [customSourceOpen, setCustomSourceOpen] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState<Item | null>(
    null
  );
  const [customSourceText, setCustomSourceText] = useState('');
  const [customReferrerOpen, setCustomReferrerOpen] = useState(false);
  const [customReferrerText, setCustomReferrerText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedJobs(defaultJob ? [defaultJob] : []);
      setSelectedDepartment(null);
      setSelectedOffice(null);
      setSelectedPool(null);
      setSelectedStage(null);
      setSelectedOwner(defaultOwner ?? null);
      setSelectedSource(null);
      setSelectedReferrer(null);
      setCustomSourceOpen(false);
      setSelectedSourceType(null);
      setCustomSourceText('');
      setCustomReferrerOpen(false);
      setCustomReferrerText('');

      if (sourceAndResponsibilityPath) {
        standardRequestWithErrorFlash
          .get(sourceAndResponsibilityPath)
          .then(({ data }) => {
            if (data.source) {
              setSelectedSource(data.source);
            }
            if (data.referrer) {
              setSelectedReferrer(data.referrer);
            }
          });
      }
    }
  }, [isOpen, defaultJob, defaultOwner, sourceAndResponsibilityPath]);

  useEffect(() => {
    setSelectedOffice(null);
  }, [selectedDepartment]);

  useEffect(() => {
    setSelectedStage(null);
  }, [selectedPool]);

  // Remount key for office dropdown when department changes
  const [officeKey, setOfficeKey] = useState(0);
  useEffect(() => {
    setOfficeKey((k) => k + 1);
  }, [selectedDepartment]);

  const fetchJobs = usePaginatedFetchItems(jobsPath, { nested: true });
  const fetchDepartments = useFetchItems(departmentsPath);
  const fetchOffices = usePaginatedFetchItems(officesPath, {
    ...(selectedDepartment ? { department_id: selectedDepartment.value } : {}),
  });
  const [stagesByPoolId, setStagesByPoolId] = useState<Record<string, Item[]>>(
    {}
  );

  const storePoolStages = useCallback((rawItems: Option[]) => {
    const map: Record<string, Item[]> = {};
    // Grouped format: each rawItem is a group with children that are pools
    rawItems.forEach((group) => {
      if (!group.children) {
        return;
      }
      const pools = group.children as unknown as ProspectPool[];
      pools.forEach((pool) => {
        map[pool.id.toString()] = (pool.prospect_pool_stages || []).map(
          (stage) => ({
            value: stage.id.toString(),
            label: stage.name,
          })
        );
      });
    });
    setStagesByPoolId(map);
  }, []);

  const fetchPools = useFetchItems(
    prospectPoolsPath,
    { grouped: 1 },
    storePoolStages,
    true
  );

  const stageOptions = selectedPool
    ? stagesByPoolId[selectedPool.value] || []
    : [];

  const fetchOwners = usePaginatedFetchItems(prospectOwnerOptionsPath);
  const fetchSources = useFetchItems(sourceOptionsPath);
  const fetchReferrers = usePaginatedFetchItems(referrerOptionsPath, {
    remove_inactive: true,
  });

  // Source type fetcher for custom source creation
  const fetchSourceTypes = useCallback(
    async (term: string): Promise<{ items: Item[] }> => {
      if (!sourceTypeOptionsPath) {
        return { items: [] };
      }
      const result = await standardRequest.get(sourceTypeOptionsPath, {
        params: { term, react_select: true },
      });
      return { items: result.data.results };
    },
    [sourceTypeOptionsPath]
  );

  const poolHelperText =
    !selectedPool && defaultPoolName
      ? t('default_pool_helper', { pool_name: defaultPoolName })
      : '';

  const jobsDropdown = useMultiDropdown({
    fetchItems: fetchJobs,
    paginated: true,
    selectedItems: selectedJobs,
    onSelectedItemsChange: (changes) =>
      setSelectedJobs(changes.selectedItems ?? []),
    preventLoadOnMount: true,
  });

  const departmentsDropdown = useSingleDropdown({
    fetchItems: fetchDepartments,
    selectedItem: selectedDepartment,
    onSelectedItemChange: (e) => setSelectedDepartment(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const officesDropdown = useSingleDropdown({
    fetchItems: fetchOffices,
    paginated: true,
    selectedItem: selectedOffice,
    onSelectedItemChange: (e) => setSelectedOffice(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const poolsDropdown = useSingleDropdown({
    fetchItems: fetchPools,
    selectedItem: selectedPool,
    onSelectedItemChange: (e) => setSelectedPool(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const ownersDropdown = useSingleDropdown({
    fetchItems: fetchOwners,
    paginated: true,
    selectedItem: selectedOwner,
    onSelectedItemChange: (e) => setSelectedOwner(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const sourcesDropdown = useSingleDropdown({
    fetchItems: fetchSources,
    selectedItem: selectedSource,
    onSelectedItemChange: (e) => setSelectedSource(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const sourceTypesDropdown = useSingleDropdown({
    fetchItems: fetchSourceTypes,
    selectedItem: selectedSourceType,
    onSelectedItemChange: (e) => setSelectedSourceType(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const referrersDropdown = useSingleDropdown({
    fetchItems: fetchReferrers,
    paginated: true,
    selectedItem: selectedReferrer,
    onSelectedItemChange: (e) => setSelectedReferrer(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const toggleCustomSource = () => {
    setCustomSourceOpen((prev) => {
      if (prev) {
        setSelectedSourceType(null);
        setCustomSourceText('');
      } else {
        setSelectedSource(null);
      }
      return !prev;
    });
  };

  const toggleCustomReferrer = () => {
    setCustomReferrerOpen((prev) => {
      if (prev) {
        setCustomReferrerText('');
      }
      return !prev;
    });
  };

  const isCustomSourceValid =
    !customSourceOpen ||
    (!!selectedSourceType && customSourceText.trim().length > 0);
  const isCustomReferrerValid =
    !customReferrerOpen || customReferrerText.trim().length > 0;
  const canSubmit = !submitting && isCustomSourceValid && isCustomReferrerValid;

  const onSubmit = async () => {
    setSubmitting(true);

    const formData = new FormData();

    selectedJobs.forEach((job) =>
      formData.append('prospective_hiring_plan_ids[]', job.value)
    );

    formData.append('referrer_application_id', applicationId);

    if (selectedDepartment) {
      formData.append('prospective_department_id', selectedDepartment.value);
    }
    if (selectedOffice) {
      formData.append('prospective_office_id', selectedOffice.value);
    }
    if (selectedPool) {
      formData.append('prospect_pool_id', selectedPool.value);
    }
    if (selectedStage) {
      formData.append('prospect_pool_stage_id', selectedStage.value);
    }
    if (selectedOwner) {
      formData.append('prospect_owner_id', selectedOwner.value);
    }

    if (customSourceOpen) {
      formData.append(
        'custom_source_strategy_id',
        (selectedSourceType as Item).value
      );
      formData.append('custom_source', customSourceText.trim());
    } else if (selectedSource) {
      formData.append('source_id', selectedSource.value);
    }

    if (customReferrerOpen) {
      formData.append('referred_by', customReferrerText.trim());
    } else if (selectedReferrer) {
      formData.append('referred_by_id', selectedReferrer.value);
    }

    try {
      await standardRequestWithFlash.post(makeProspectPath, formData, {
        successMessage: t('success'),
      });
      // Invalidate dropdown caches so new custom sources/referrers appear
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            key[0] === 'prospect-modal' &&
            (key[1] === sourceOptionsPath || key[1] === referrerOptionsPath)
          );
        },
      });
      onSuccess?.({
        ...Object.fromEntries(formData),
        'prospective_hiring_plan_ids[]': selectedJobs.map((job) => job.value),
      });
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title', { name: fullName })}
      size="medium"
    >
      <DataEntryModal.Header onClick={closeModal}>
        <span className="pii-blur">{t('title', { name: fullName })}</span>
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <FullWidth>
          <Stack spacing={3} sx={{ paddingBottom: 3 }}>
            {/* Job, department, office section */}
            <Stack spacing={3}>
              <SectionHeader>{t('job_department_office')}</SectionHeader>

              <MultiDropdown dropdownProps={jobsDropdown}>
                <MultiDropdown.Field
                  label={t('job_name')}
                  placeholder={t('job_name_placeholder')}
                />
                <MultiDropdown.Flyout />
              </MultiDropdown>

              <SecondaryBody>{t('andor')}</SecondaryBody>

              <SingleDropdown dropdownProps={departmentsDropdown}>
                <SingleDropdown.Field
                  label={t('department')}
                  placeholder={t('department_placeholder')}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>

              <SingleDropdown
                key={`office-${officeKey}`}
                dropdownProps={officesDropdown}
              >
                <SingleDropdown.Field
                  label={t('office')}
                  disabled={!selectedDepartment}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>
            </Stack>

            <Divider />

            {/* Pool section */}
            <Stack spacing={3}>
              <SectionHeader>{t('pool_section')}</SectionHeader>

              <SingleDropdown
                key={`pool-${selectedPool?.value}`}
                dropdownProps={poolsDropdown}
              >
                <SingleDropdown.Field
                  label={t('pool')}
                  placeholder={t('pool_placeholder')}
                  helperText={poolHelperText}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>

              <SimpleSingleDropdown
                key={`stage-${selectedPool?.value ?? 'none'}`}
                label={t('stage')}
                placeholder={t('stage_placeholder')}
                items={stageOptions}
                selectedItem={selectedStage}
                onSelectedItemChange={setSelectedStage}
                disabled={!selectedPool || stageOptions.length === 0}
              />
            </Stack>

            <Divider />

            {/* Owner, referrer, and source section */}
            <Stack spacing={3}>
              <SectionHeader>{t('owner_referrer_source')}</SectionHeader>

              <div className="pii-blur">
                <SingleDropdown dropdownProps={ownersDropdown}>
                  <SingleDropdown.Field
                    label={t('owner')}
                    placeholder={t('owner_placeholder')}
                  />
                  <SingleDropdown.Flyout />
                </SingleDropdown>
              </div>

              <Stack spacing={1}>
                <div className="pii-blur">
                  {customSourceOpen ? (
                    <Stack spacing={3}>
                      <SingleDropdown dropdownProps={sourceTypesDropdown}>
                        <SingleDropdown.Field
                          label={t('source_type')}
                          placeholder={t('source_type_placeholder')}
                          required
                        />
                        <SingleDropdown.Flyout />
                      </SingleDropdown>
                      <TextInput
                        label={t('custom_source')}
                        value={customSourceText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCustomSourceText(e.target.value)
                        }
                        required
                      />
                    </Stack>
                  ) : (
                    <SingleDropdown dropdownProps={sourcesDropdown}>
                      <SingleDropdown.Field
                        label={t('source')}
                        placeholder={t('source_placeholder')}
                      />
                      <SingleDropdown.Flyout />
                    </SingleDropdown>
                  )}
                </div>
                {canCreateSources && (
                  <Stack alignItems="flex-start">
                    <TertiaryButton size="md" onClick={toggleCustomSource}>
                      {customSourceOpen
                        ? t('existing_source')
                        : t('add_source')}
                    </TertiaryButton>
                  </Stack>
                )}
              </Stack>

              <Stack spacing={1}>
                <div className="pii-blur">
                  {customReferrerOpen ? (
                    <TextInput
                      label={t('referrer')}
                      value={customReferrerText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomReferrerText(e.target.value)
                      }
                      required
                    />
                  ) : (
                    <SingleDropdown dropdownProps={referrersDropdown}>
                      <SingleDropdown.Field
                        label={t('referrer')}
                        placeholder={t('referrer_placeholder')}
                      />
                      <SingleDropdown.Flyout />
                    </SingleDropdown>
                  )}
                </div>
                {canCreateSources && (
                  <Stack alignItems="flex-start">
                    <TertiaryButton size="md" onClick={toggleCustomReferrer}>
                      {customReferrerOpen
                        ? t('existing_referrer')
                        : t('add_referrer')}
                    </TertiaryButton>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </FullWidth>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SecondaryButton onClick={closeModal} disabled={submitting}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton onClick={onSubmit} disabled={!canSubmit}>
            {t('save')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default MakeProspectModal;
